import {
  type CoreResume,
  type FResume,
  type RecursivePartial,
  type ResumeMetadata,
  type Resumes,
  SCHEMA_VERSION,
  type UniqueDocIdentifier,
  type UniqueIdentifier,
} from "@piperunner-cms/types/v1";
import { and, collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { FIRESTORE_COLLECTION_PATH, NotFoundError } from "@/constants";
import {
  doesDocExist,
  doesDocumentBelongToUser,
  isDocumentIdentifierPresent,
  isUniqueIdentifierPresent,
  normalizeObject,
} from "@/utils";
import { fsDb } from "../utils/initialize-fs-db";

/**
 * Fetches resumes for a given user and project.
 * @param userId - The ID of the user whose resumes are to be fetched.
 * @param projectId - The ID of the project for which resumes are to be fetched.
 * @returns A promise that resolves to an object containing resumes.
 * @throws NotFoundError if no resumes are found for the given userId and projectId.
 */
export const getResumes = async (identifier: UniqueIdentifier): Promise<Resumes> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.RESUMES);
  const docQuery = query(collectionRef, and(where("userId", "==", userId), where("projectId", "==", projectId)));
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    throw new NotFoundError(`No resume found for userId: ${userId} and projectId: ${projectId}`);
  }

  const resumes = docSnap.docs.reduce<Resumes>((acc, doc) => {
    acc[doc.id] = {
      id: doc.id,
      payload: {
        name: doc.data().name,
        description: doc.data().description || null,
        projectId: doc.data().projectId,
        userId: doc.data().userId,
        id: doc.data().id,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        publishedAt: doc.data().publishedAt,
        latest: doc.data().latest || null,
        draft: doc.data().draft || null,
        schemaVersion: doc.data().schemaVersion || 1,
        revision: doc.data().revision || 0,
      },
    };
    return acc;
  }, {});

  return resumes;
};

/**
 * Creates a new resume document in Firestore.
 * @param identifier - The unique identifier for the user and project.
 * @param metadata - Metadata for the resume, including name and description.
 * @returns A promise that resolves to the created resume document.
 * @throws Error if the document creation fails.
 */
export const createResume = async (identifier: UniqueIdentifier, metadata: ResumeMetadata): Promise<FResume> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.RESUMES);
  const docRef = doc(collectionRef);

  const resumeData: FResume = {
    userId,
    projectId,
    id: docRef.id,
    name: metadata.name,
    description: metadata.description ?? null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    publishedAt: null,
    latest: null,
    draft: null,
    schemaVersion: SCHEMA_VERSION,
    revision: 1,
  };

  await setDoc(docRef, resumeData);

  return resumeData;
};

export const updateResume = async (
  identifier: UniqueDocIdentifier,
  data: RecursivePartial<CoreResume>,
): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.RESUMES, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    updatedAt: new Date().toISOString(),
    draft: normalizeObject(data),
  } as RecursivePartial<FResume>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const resetResume = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.RESUMES, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const resetData = {
    draft: null,
  } as RecursivePartial<FResume>;

  await setDoc(docRef, resetData, { merge: true });
};

export const publishResume = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.RESUMES, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    revision: docSnap.data().revision + 1,
    updatedAt: new Date().toISOString(),
    publishedAt: new Date().toISOString(),
    latest: docSnap.data().draft,
    draft: null,
  } as RecursivePartial<FResume>;

  await setDoc(docRef, updatedData, { merge: true });
};
